// Column ids, server sort/search columns and form field names for Manage customers.

export const CUSTOMER_COLUMNS = Object.freeze({
  serialNo: 'serialNo',
  customerName: 'customerName',
  customerId: 'customerId',
  shippingSiteCount: 'shippingSiteCount',
  primaryContact: 'primaryContact',
  lastUpdated: 'lastUpdated',
});

// TODO: sorting to be added later — GET /customers answers any `sort` param with a 500
export const CUSTOMER_SORT_FIELDS = Object.freeze({});

// Real columns only; the middleware silently drops a key it cannot match.
export const CUSTOMER_SEARCH_IN = Object.freeze(['companyname', 'customer_id', 'email']);

export const CUSTOMER_SITE_COLUMNS = Object.freeze({
  serialNo: 'serialNo',
  shippingTo: 'shippingTo',
  shippingSiteId: 'shippingSiteId',
  managerEmail: 'managerEmail',
  managerName: 'managerName',
  isccCertificateNumber: 'isccCertificateNumber',
  validTill: 'validTill',
  status: 'status',
  addressLine1: 'addressLine1',
  addressLine2: 'addressLine2',
  city: 'city',
  country: 'country',
  zipCode: 'zipCode',
  actions: 'actions',
});

// Column id → `shipping_details` column, for GET /customers/:id?tab=site_details.
export const CUSTOMER_SITE_SORT_FIELDS = Object.freeze({
  [CUSTOMER_SITE_COLUMNS.shippingTo]: 'shipping_from',
  [CUSTOMER_SITE_COLUMNS.shippingSiteId]: 'shipping_id',
  [CUSTOMER_SITE_COLUMNS.managerEmail]: 'email',
  [CUSTOMER_SITE_COLUMNS.managerName]: 'site_manager_name',
  [CUSTOMER_SITE_COLUMNS.isccCertificateNumber]: 'certificate_number',
  [CUSTOMER_SITE_COLUMNS.validTill]: 'valid_to',
  [CUSTOMER_SITE_COLUMNS.city]: 'city',
  [CUSTOMER_SITE_COLUMNS.zipCode]: 'zip_code',
});

export const CUSTOMER_SITE_SEARCH_IN = Object.freeze(['shipping_from', 'shipping_id']);

export const CUSTOMER_SITE_DETAILS_TAB = 'site_details';

export const CUSTOMER_ID_PREFIX = 'CI';
export const CUSTOMER_SITE_ID_PREFIX = 'SI';

export const ORGANIZATION_NAME_MAX_LENGTH = 80;
// RFC 5321's cap on a full address.
export const EMAIL_MAX_LENGTH = 320;

export const CUSTOMER_FIELDS = Object.freeze({
  organizationName: 'organizationName',
  idPrefix: 'idPrefix',
  idNumber: 'idNumber',
  primaryEmail: 'primaryEmail',
  primaryContactName: 'primaryContactName',
  addressLine1: 'addressLine1',
  addressLine2: 'addressLine2',
  city: 'city',
  country: 'country',
  zipCode: 'zipCode',
});

export const REQUIRED_CUSTOMER_FIELDS = Object.freeze([
  CUSTOMER_FIELDS.organizationName,
  CUSTOMER_FIELDS.idNumber,
  CUSTOMER_FIELDS.addressLine1,
  CUSTOMER_FIELDS.city,
  CUSTOMER_FIELDS.country,
  CUSTOMER_FIELDS.zipCode,
]);

export const EMPTY_CUSTOMER_FORM = Object.freeze({
  [CUSTOMER_FIELDS.organizationName]: '',
  [CUSTOMER_FIELDS.idPrefix]: CUSTOMER_ID_PREFIX,
  [CUSTOMER_FIELDS.idNumber]: '',
  [CUSTOMER_FIELDS.primaryEmail]: '',
  [CUSTOMER_FIELDS.primaryContactName]: '',
  [CUSTOMER_FIELDS.addressLine1]: '',
  [CUSTOMER_FIELDS.addressLine2]: '',
  [CUSTOMER_FIELDS.city]: '',
  [CUSTOMER_FIELDS.country]: '',
  [CUSTOMER_FIELDS.zipCode]: '',
});

export const SITE_FIELDS = Object.freeze({
  id: 'id',
  shippingTo: 'shippingTo',
  idPrefix: 'idPrefix',
  idNumber: 'idNumber',
  managerName: 'managerName',
  managerEmail: 'managerEmail',
  isccCertificateNumber: 'isccCertificateNumber',
  isccVerified: 'isccVerified',
  validFrom: 'validFrom',
  validTill: 'validTill',
  addressLine1: 'addressLine1',
  addressLine2: 'addressLine2',
  city: 'city',
  country: 'country',
  zipCode: 'zipCode',
});

// TODO: lift the cap once bulk site import exists
export const MAX_SHIPPING_SITES = 5;
export const MIN_SHIPPING_SITES = 1;

export const SITE_KEYS_FIELD = 'siteKeys';
export const FIRST_SITE_KEY = '1';

// ReferenceIdField's `namePrefix` — every site card's fields live under `sites.<key>.`.
export const siteNamePrefix = (siteKey) => `sites.${siteKey}.`;

export const siteFieldName = (siteKey, field) => `${siteNamePrefix(siteKey)}${field}`;

export const nextSiteKey = (siteKeys) =>
  String(siteKeys.reduce((highest, key) => Math.max(highest, Number(key) || 0), 0) + 1);

export const emptySiteValues = (siteKey) => ({
  ...Object.fromEntries(
    Object.values(SITE_FIELDS).map((field) => [siteFieldName(siteKey, field), '']),
  ),
  [siteFieldName(siteKey, SITE_FIELDS.idPrefix)]: CUSTOMER_SITE_ID_PREFIX,
  [siteFieldName(siteKey, SITE_FIELDS.isccVerified)]: false,
});

export const EMPTY_SITES_FORM = Object.freeze({
  [SITE_KEYS_FIELD]: [FIRST_SITE_KEY],
  ...emptySiteValues(FIRST_SITE_KEY),
});
